// Videollamada P2P entre trabajadores (WebRTC). El audio/vídeo va DIRECTO entre navegadores; el
// servidor (namespace /llamadas) solo hace señalización. Malla (mesh) para grupos pequeños: el que
// ENTRA a la sala crea las ofertas a los que ya estaban → sin colisión de ofertas. Provee llamar()
// y renderiza la UI de llamada entrante y en curso. Reutiliza el socket.io del proyecto.
import { createContext, useContext, useEffect, useRef, useState } from 'react'
import { io } from 'socket.io-client'
import api from '../services/api'
import { useAuth } from './AuthContext'
import { useToast } from './ToastContext'
import Icon from '../components/Icon'
import { esEdicionLocal } from '../utils/edicionLocalEstado'
import { idDelDispositivo } from '../utils/dispositivo'   // t530 · B-2268 · qué ordenador es (la ficha se abre solo en el de ese gabinete)
import { t as i18nT, tx, marca, tDato } from '../utils/i18n'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

// ══════════════════════════════════════════════════════════════════════════════════════════════
//  EL TONO DE LLAMADA DEL CHAT DEL EQUIPO — Y ES DE CADA PERSONA           (B-251)
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
//  VIVE AQUI DENTRO, y no en un `services/` aparte, POR LA MISMA RAZON QUE COSTO 18 HORAS DE
//  SERVIDOR EL 2-sep: este fichero esta versionado y uno nuevo no lo estaria. Un fichero versionado
//  que importa uno que no viaja compila en el ordenador de quien lo escribe y REVIENTA el
//  `npm run build` del despliegue — y mientras tanto el servidor sigue sirviendo el paquete viejo y
//  no llega NADA de lo que se sube. Metido aqui, el import huerfano no existe: o viaja este fichero
//  entero, o no viaja nada. Y ademas es su sitio: el tono es de las llamadas, y las llamadas son
//  esto. «Mi perfil» lo importa de aqui.
//
//  QUE HABIA. Las llamadas y videollamadas entre compañeros (el boton del telefono del chat del
//  equipo) sonaban con `/notif.mp3` EN BUCLE. Ese fichero es el aviso corto de las notificaciones
//  —lo usan tambien los avisos de envios, el modo doctor y el sondeo de WhatsApp—, asi que una
//  llamada entrante sonaba igual que un mensaje, repetido. Y no habia forma de cambiarlo.
//
//  QUE HAY EN EL REPO, medido antes de escribir nada: UN SOLO fichero de sonido, `public/notif.mp3`.
//  Ninguno mas. Por eso los tonos nuevos NO son ficheros del repositorio: se SINTETIZAN aqui mismo
//  (t398: como un WAV en memoria que suena en bucle con el mismo reproductor que el de siempre; ver
//  crearTono). Cero binarios nuevos, cero peso en el arranque, y el mismo tono suena igual en el
//  movil y en el ordenador.
//
//  POR PERSONA, NO POR CLINICA. Un tono es de quien lo oye: en la misma clinica, quien esta en
//  recepcion quiere oirlo y quien esta en el gabinete con un paciente delante no. Se guarda en las
//  PREFERENCIAS DEL AGENTE (`agentes.preferencias`, JSONB por usuario, la misma puerta que ya usa
//  «Al entrar, llevame a…»), asi que viaja con la cuenta: se elige una vez y vale tambien en el
//  otro ordenador y en el movil. El espejo en el navegador es solo para que la PRIMERA llamada de
//  la sesion suene con el tono bueno sin esperar a una peticion.
//
//  EL DEFECTO NO CAMBIA PARA NADIE. Quien no elija nada sigue oyendo exactamente lo que oia ayer
//  (`clasico` = notif.mp3 en bucle). Cambiar el sonido de todo el mundo por sorpresa no es lo que se
//  pidio: lo que se pidio es poder cambiarlo.

// El catalogo. `que` es la frase que se lee en la pantalla de perfil: sin ella, elegir un tono es
// probar cinco veces a ver cual es cual.
export const TONOS = [
  { clave: 'clasico', nombre: 'El de siempre',
    que: 'El aviso corto de la aplicación, repetido. Es el que suena si no eliges nada.' },
  { clave: 'timbre', nombre: 'Timbre de teléfono',
    que: 'Dos toques y una pausa, como un teléfono de sobremesa. Se distingue de un aviso.' },
  { clave: 'suave', nombre: 'Suave',
    que: 'Tres notas cortas que suben. Suena menos estridente en un gabinete.' },
  { clave: 'discreto', nombre: 'Discreto',
    que: 'Un toque grave cada pocos segundos. Casi no se oye desde la sala de espera.' },
  { clave: 'mudo', nombre: 'Sin sonido',
    que: 'No suena nada: la llamada solo aparece en la pantalla. Para cuando estás con un paciente.' },
]

export const TONO_POR_DEFECTO = 'clasico'

// El fichero que ya existe en el repo. Se deja escrito una vez: si algun dia se cambia, se cambia
// aqui y no en cuatro sitios.
export const AVISO_MP3 = '/notif.mp3'

// LOS TONOS SINTETIZADOS. Cada nota es [frecuencia en Hz, cuando empieza en segundos, cuanto dura].
// `cada` es lo que tarda en repetirse el patron completo, en milisegundos: una vuelta del fichero
// que suena en bucle (muestrasDelTono).
const PATRONES = {
  timbre:   { onda: 'square', volumen: 0.13, cada: 3000, notas: [[480, 0, 0.40], [480, 0.55, 0.40]] },
  suave:    { onda: 'sine',   volumen: 0.16, cada: 2800, notas: [[523.25, 0, 0.30], [659.25, 0.18, 0.30], [783.99, 0.36, 0.40]] },
  discreto: { onda: 'sine',   volumen: 0.11, cada: 4000, notas: [[320, 0, 0.14]] },
}

const G = () => (typeof globalThis !== 'undefined' ? globalThis : {})

// ¿Es una clave de tono de las que existen? Todo lo que entra por la red o por el navegador pasa por
// aqui: una preferencia con basura dentro tiene que caer al tono de siempre, nunca dejar la llamada
// muda por accidente (que es justo lo que no se puede perder: una llamada que no suena no existe).
export function tonoValido(clave) {
  const c = String(clave == null ? '' : clave).trim()
  return TONOS.some(t => t.clave === c) ? c : null
}

export const tonoDe = (clave) => TONOS.find(t => t.clave === tonoValido(clave)) || TONOS.find(t => t.clave === TONO_POR_DEFECTO)

// ── EL ESPEJO EN EL NAVEGADOR ────────────────────────────────────────────────────────────────
// La preferencia de verdad vive en la cuenta (servidor). Esto es solo para que la PRIMERA llamada
// de la sesion ya suene bien, sin esperar a que llegue la peticion. Va por usuario: en recepcion el
// mismo ordenador lo usan varias personas y el tono de una no puede ser el de la siguiente.
const claveLS = (usuarioId) => 'tonoLlamada:' + (usuarioId || 'anon')

export function leerTonoLocal(usuarioId) {
  try { return tonoValido(G().localStorage && G().localStorage.getItem(claveLS(usuarioId))) } catch (_) { return null }
}

export function guardarTonoLocal(clave, usuarioId) {
  try {
    const ls = G().localStorage
    if (!ls) return
    const c = tonoValido(clave)
    if (c) ls.setItem(claveLS(usuarioId), c); else ls.removeItem(claveLS(usuarioId))
  } catch (_) { /* navegador sin almacenamiento: se usara el tono de siempre */ }
}

// ── EL REPRODUCTOR ───────────────────────────────────────────────────────────────────────────
//
// Devuelve siempre un objeto con `sonar()` y `parar()`, pase lo que pase. Si el navegador no deja
// sonar (una politica de autoplay, un AudioContext que no arranca), la llamada SE SIGUE VIENDO en
// pantalla: aqui nunca se lanza una excepcion hacia arriba, porque quien llama a esto esta en mitad
// de pintar una llamada entrante.
//
// `entorno` es para poder medirlo: { Audio }. En el navegador no se pasa nada.
//
// t398 (22-oct-2026) · TODOS LOS TONOS SUENAN EN BUCLE DE LA MISMA FORMA. El dueño: con «El de siempre» el busca sonaba
// sin parar y con «Suave» sonaba UNA vez y se paraba. «El de siempre» es un <audio> con `loop`: lo repite el propio
// reproductor del navegador. Los sintetizados tocaban una vuelta con el Web Audio API y la siguiente la pedía un
// setInterval de JavaScript, y ese temporizador es justo lo que el navegador frena en una pestaña en segundo plano y el
// móvil congela con la app de fondo o la pantalla apagada: la primera vuelta sonaba y las demás no llegaban. Ahora cada
// tono sintetizado se compone UNA vez como un fichero WAV en memoria (una vuelta entera del patrón, con su silencio) y
// suena con el MISMO reproductor que «El de siempre»: un <audio> con `loop`, hasta que se para. Sigue sin haber ningún
// fichero de sonido nuevo en el repositorio. Lo mide backend/routes/tono-llamada-chat.test.js §T7.
export function crearTono(clave, entorno = {}) {
  const def = tonoDe(clave)
  if (def.clave === 'mudo') {
    return { clave: 'mudo', suena: false, enBucle: false, fichero: null, sonar() {}, parar() {} }
  }
  const AudioCls = entorno.Audio || G().Audio
  const fichero = def.clave === 'clasico' ? AVISO_MP3 : urlDelTono(def.clave)
  let a = null
  return {
    clave: def.clave, suena: true, enBucle: true, fichero,
    sonar() {
      try {
        if (!AudioCls || !fichero) return
        if (!a) { a = new AudioCls(fichero); a.loop = true }
        a.currentTime = 0
        const p = a.play()
        if (p && typeof p.catch === 'function') p.catch(() => {})
      } catch (_) {}
    },
    parar() { try { if (a) { a.pause(); a.currentTime = 0 } } catch (_) {} },
  }
}

// ── LOS TONOS SINTETIZADOS, COMO FICHERO WAV EN MEMORIA ──────────────────────────────────────
// Una vuelta del patrón (`cada` milisegundos, notas y silencio) en muestras de 16 bits, mono. Las notas, con la misma
// forma que tenían en el Web Audio API: entrada de 20 ms y salida en rampa hasta el final de la nota (un tono que arranca
// o corta en seco chasquea en los altavoces). La onda cuadrada, con sus armónicos impares por debajo de la mitad de la
// frecuencia de muestreo, como la hace el navegador, para que no raspe.
export const MUESTREO_TONOS = 22050

export function muestrasDelTono(clave, muestreo = MUESTREO_TONOS) {
  const patron = PATRONES[clave]
  if (!patron) return null
  const n = Math.round((patron.cada / 1000) * muestreo)
  const out = new Float32Array(n)
  for (const [hz, cuando, dura] of patron.notas) {
    const armonicos = []
    if (patron.onda === 'square') { for (let k = 1; hz * k < muestreo * 0.45; k += 2) armonicos.push([k, 4 / (Math.PI * k)]) }
    else armonicos.push([1, 1])
    const i0 = Math.round(cuando * muestreo)
    const i1 = Math.min(n, Math.round((cuando + dura) * muestreo))
    for (let i = i0; i < i1; i++) {
      const t = (i - i0) / muestreo
      const g = t < 0.02 ? patron.volumen * (t / 0.02) : patron.volumen * Math.max(0, 1 - (t - 0.02) / Math.max(0.001, dura - 0.02))
      let s = 0
      for (const [k, amp] of armonicos) s += amp * Math.sin(2 * Math.PI * hz * k * t)
      out[i] += g * s
    }
  }
  return out
}

export function wavDeMuestras(muestras, muestreo = MUESTREO_TONOS) {
  const n = muestras.length
  const v = new DataView(new ArrayBuffer(44 + n * 2))
  const txt = (o, s) => { for (let i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i)) }
  txt(0, 'RIFF'); v.setUint32(4, 36 + n * 2, true); txt(8, 'WAVE')
  txt(12, 'fmt '); v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 1, true)
  v.setUint32(24, muestreo, true); v.setUint32(28, muestreo * 2, true); v.setUint16(32, 2, true); v.setUint16(34, 16, true)
  txt(36, 'data'); v.setUint32(40, n * 2, true)
  for (let i = 0; i < n; i++) v.setInt16(44 + i * 2, Math.round(Math.max(-1, Math.min(1, muestras[i])) * 32767), true)
  return new Uint8Array(v.buffer)
}

// La dirección del fichero de cada tono (data:audio/wav). Se compone la primera vez que suena y se guarda: es un sonido,
// igual para todo el mundo, no un dato de nadie. Si no se puede componer, null (y crearTono no suena, pero no revienta).
const _urlsDeTonos = new Map()
export function urlDelTono(clave) {
  if (_urlsDeTonos.has(clave)) return _urlsDeTonos.get(clave)
  let url = null
  try {
    const m = muestrasDelTono(clave)
    if (m && typeof G().btoa === 'function') {
      const bytes = wavDeMuestras(m)
      let bin = ''
      for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000))
      url = 'data:audio/wav;base64,' + G().btoa(bin)
    }
  } catch (_) { url = null }
  if (url) _urlsDeTonos.set(clave, url)
  return url
}

const LlamadasContext = createContext(null)
export const useLlamadas = () => useContext(LlamadasContext) || { estado: 'idle', llamar: () => {}, llamarGrupo: () => {} }

// Socket del namespace /llamadas (singleton). autoConnect false: conectamos al tener sesión.
let _sock = null
function getSock() {
  if (!_sock) {
    _sock = io('/llamadas', {
      path: '/api/socket.io',
      // B-914 · EL TOKEN SE LEE EN CADA CONEXIÓN, también al reconectar. Con un objeto fijo, la reconexión entraba con el
      // token con el que se abrió; si la sesión se había renovado, el servidor la rechazaba y la conexión se quedaba muerta
      // sin avisar: ni llamadas ni busca en ese dispositivo hasta recargar.
      auth: (cb) => cb({ token: localStorage.getItem('token') }),
      // t530 · B-2268 · el identificador de ESTE ordenador (utils/dispositivo): el servidor manda la ficha del paciente que
      // pasa a gabinete solo a los sockets del ordenador que está en ese gabinete (services/pacienteEnGabinete).
      query: { dispositivo: idDelDispositivo() || '' },
      transports: ['polling', 'websocket'],
      autoConnect: false,
    })
    // B-914 · al (re)conectar, el busca pregunta al servidor qué sigue sonando: lo que llegó o se contestó mientras la
    // conexión estaba caída no se pierde (components/busca/BuscaEntrante).
    _sock.on('connect', () => { try { window.dispatchEvent(new CustomEvent('biango-busca', { detail: { tipo: 'sincronizar' } })) } catch (_) { } })
    _sock.on('connect_error', (e) => {
      console.warn('[llamadas] connect_error:', e?.message || e)
      // B-914 · si la rechaza el SERVIDOR (no la red), socket.io no lo vuelve a intentar solo: se reintenta en 15 s mientras
      // haya sesión (el token se vuelve a leer al conectar).
      if (!_sock.active) setTimeout(() => { if (_sock && !_sock.connected && localStorage.getItem('token')) _sock.connect() }, 15000)
    })
  }
  return _sock
}

export function LlamadasProvider({ children }) {
  const { user } = useAuth()
  const toast = useToast()
  const [estado, setEstado] = useState('idle')     // idle | llamando | entrante | en-llamada
  const [entrante, setEntrante] = useState(null)   // {sala, video, de:{id,nombre,avatar_b64}}
  const [llamandoA, setLlamandoA] = useState(null) // usuario al que llamamos (UI)
  const [remotos, setRemotos] = useState([])       // [{socketId, agente, stream}]
  const [localStream, setLocalStream] = useState(null)
  const [micOn, setMicOn] = useState(true)
  const [camOn, setCamOn] = useState(true)
  const [conVideo, setConVideo] = useState(true)

  const localRef = useRef(null)
  const peersRef = useRef({})       // socketId → { pc, agente }
  const salaRef = useRef(null)
  const destRef = useRef(null)      // id del usuario con quien hablamos (para cancelar/rechazar)
  const iceRef = useRef([{ urls: ['stun:stun.l.google.com:19302', 'stun:stun1.l.google.com:19302'] }])
  const ringRef = useRef(null)
  const tonoRef = useRef(null)      // la clave del tono elegido por ESTA persona (B-251)
  const estadoRef = useRef('idle')
  const timeoutRef = useRef(null)   // temporizador de "sin respuesta" (saliente y entrante)
  const setEst = (v) => { estadoRef.current = v; setEstado(v) }
  const limpiarTimeout = () => { if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null } }

  // EL TONO ES EL QUE HAYA ELEGIDO ESTA PERSONA (B-251). Antes esto era `new Audio('/notif.mp3')`
  // clavado: una llamada entrante sonaba igual que un mensaje de WhatsApp, en bucle, y no habia
  // forma de cambiarlo. La clave elegida vive en `tonoRef` (ver el efecto de abajo) y el catalogo
  // en services/tonosLlamada — que ademas SINTETIZA los tonos, para no meter binarios nuevos.
  //
  // Se construye el reproductor EN CADA TIMBRE, no una vez, y la clave se relee del espejo del
  // navegador justo antes de sonar: asi cambiar el tono en «Mi perfil» vale YA, en la siguiente
  // llamada, sin recargar la aplicacion. Este contexto se monta una sola vez al entrar, asi que si
  // solo mirara lo que leyo entonces, quien acabara de cambiar el tono seguiria oyendo el viejo
  // hasta el dia siguiente — y creeria que el ajuste no funciona.
  const ring = (on) => {
    try {
      if (on) {
        if (ringRef.current) ringRef.current.parar()
        ringRef.current = crearTono(leerTonoLocal(user?.id) || tonoRef.current)
        ringRef.current.sonar()
      } else if (ringRef.current) { ringRef.current.parar(); ringRef.current = null }
    } catch (_) {}
  }

  const iniciarLocal = async (video) => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: video ? { width: { ideal: 640 }, height: { ideal: 480 } } : false })
    localRef.current = stream
    setLocalStream(stream); setMicOn(true); setCamOn(!!video)
    return stream
  }

  // La conexión con un peer no llegó a establecerse (ICE 'failed' o no conecta en 25s). Suele ser
  // NAT restrictivo sin TURN. Avisa y cierra ese peer (si era el único, cerrarPeer termina la llamada)
  // para no quedarse en "Conectando…" con la cámara encendida indefinidamente.
  const conexionFallida = (socketId) => {
    const p = peersRef.current[socketId]
    if (!p || p.fallida) return
    p.fallida = true
    toast.error(i18nT('context.LlamadasContext.no_se_pudo_conectar_con', 'No se pudo conectar con {{x}} (la red puede estar bloqueando la llamada).', { x: p.agente?.nombre || i18nT('context.LlamadasContext.el_companero', 'el compañero') }))
    cerrarPeer(socketId)
  }

  const crearPeer = (socketId, agente, iniciador) => {
    const s = getSock()
    // En la edición local (Biango en la clínica, sin internet), sin servidores de fuera: solo la red de la clínica (t459).
    const pc = new RTCPeerConnection({ iceServers: esEdicionLocal() ? [] : iceRef.current })
    if (localRef.current) localRef.current.getTracks().forEach(t => pc.addTrack(t, localRef.current))
    pc.onicecandidate = (e) => { if (e.candidate) s.emit('llamada:senal', { a: socketId, sala: salaRef.current, datos: { tipo: 'ice', candidate: e.candidate } }) }
    pc.ontrack = (e) => setRemotos(prev => prev.map(r => r.socketId === socketId ? { ...r, stream: e.streams[0] } : r))
    // Vigilante de conexión: si no llega a 'connected'/'completed', corta y avisa (no cuelgue infinito).
    pc.onconnectionstatechange = () => {
      const p = peersRef.current[socketId]; if (!p) return
      if (pc.connectionState === 'connected' || pc.connectionState === 'completed') { clearTimeout(p.watchdog); p.watchdog = null }
      else if (pc.connectionState === 'failed') conexionFallida(socketId)
    }
    // pendientes: candidatos ICE que llegan ANTES de tener la descripción remota; se aplican en cuanto
    // se fija (si no, addIceCandidate lanza y se perderían → llamadas que no conectan entre redes).
    peersRef.current[socketId] = {
      pc, agente, pendientes: [],
      watchdog: setTimeout(() => { if (pc.connectionState !== 'connected' && pc.connectionState !== 'completed') conexionFallida(socketId) }, 25000),
    }
    setRemotos(prev => prev.some(r => r.socketId === socketId) ? prev : [...prev, { socketId, agente, stream: null }])
    if (iniciador) {
      pc.createOffer().then(o => pc.setLocalDescription(o))
        .then(() => s.emit('llamada:senal', { a: socketId, sala: salaRef.current, datos: { tipo: 'offer', sdp: pc.localDescription } }))
        .catch(() => {})
    }
    return pc
  }

  // Aplica los candidatos ICE que estaban en espera (ya hay descripción remota).
  const flushIce = async (socketId) => {
    const p = peersRef.current[socketId]
    if (!p) return
    const pend = p.pendientes; p.pendientes = []
    for (const c of pend) { try { await p.pc.addIceCandidate(c) } catch (_) {} }
  }

  const unirse = (sala) => {
    salaRef.current = sala
    getSock().emit('llamada:unirse', { sala }, ({ peers } = {}) => {
      (peers || []).forEach(p => crearPeer(p.socketId, p.agente, true))   // el que entra ofrece
    })
  }

  const cerrarPeer = (socketId) => {
    const p = peersRef.current[socketId]
    if (p) { clearTimeout(p.watchdog); try { p.pc.close() } catch (_) {} delete peersRef.current[socketId] }
    setRemotos(prev => {
      const next = prev.filter(r => r.socketId !== socketId)
      if (next.length === 0 && estadoRef.current !== 'idle') setTimeout(() => terminar(), 0)
      return next
    })
  }

  // Detiene la cámara/micrófono locales (por si se acaba de adquirir tras cancelar a mitad).
  const pararLocal = () => { if (localRef.current) { localRef.current.getTracks().forEach(t => t.stop()); localRef.current = null } setLocalStream(null) }

  // Limpia TODO el estado local de la llamada (no emite nada a la red).
  const terminar = () => {
    limpiarTimeout()
    Object.values(peersRef.current).forEach((p) => { clearTimeout(p.watchdog); try { p.pc.close() } catch (_) {} })
    peersRef.current = {}
    pararLocal()
    setRemotos([]); salaRef.current = null; destRef.current = null
    setLlamandoA(null); setEst('idle'); ring(false)
  }

  const llamar = async (usuario, video = true) => {
    if (!usuario?.id) return
    if (estadoRef.current !== 'idle') return toast.error(i18nT('context.LlamadasContext.ya_estas_en_una_llamada', 'Ya estás en una llamada'))
    // Reservar el estado ANTES de pedir permisos (getUserMedia es async): un segundo clic ya no pasa.
    setConVideo(video); setLlamandoA(usuario); setEst('llamando')
    try { await iniciarLocal(video) } catch (e) { terminar(); return toast.error(i18nT('context.LlamadasContext.no_se_pudo_acceder_a', 'No se pudo acceder a la cámara o el micrófono')) }
    if (estadoRef.current !== 'llamando') { pararLocal(); return }   // colgaron mientras pedía permisos
    const sala = 'call-' + usuario.id + '-' + (user?.id || '') + '-' + Math.random().toString(36).slice(2, 8)
    salaRef.current = sala; destRef.current = usuario.id
    getSock().emit('llamada:iniciar', { aUsuarioId: usuario.id, sala, video }, (ack) => {
      if (estadoRef.current !== 'llamando') return   // ya se canceló
      if (!ack?.ok) { toast.error(ack?.error || i18nT('context.LlamadasContext.no_se_pudo_llamar', 'No se pudo llamar')); terminar(); return }
      if (!ack.conectado) { toast.error(i18nT('context.LlamadasContext.no_esta_conectado_ahora_mismo', '{{x}} no está conectado ahora mismo', { x: (usuario.nombre || i18nT('context.LlamadasContext.ese_companero', 'Ese compañero')) })); terminar(); return }
      unirse(sala)
      // Sin respuesta en 40s → colgar (no dejar cámara/mic encendidos indefinidamente).
      timeoutRef.current = setTimeout(() => { if (estadoRef.current === 'llamando') { toast.error(i18nT('context.LlamadasContext.no_ha_contestado', 'No ha contestado')); colgar() } }, 40000)
    })
  }

  // Llamar a un GRUPO del chat: suena a todos sus miembros; los que acepten entran a la misma sala (mesh).
  const llamarGrupo = async (canal, video = true) => {
    if (!canal?.id) return
    if (estadoRef.current !== 'idle') return toast.error(i18nT('context.LlamadasContext.ya_estas_en_una_llamada', 'Ya estás en una llamada'))
    setConVideo(video); setLlamandoA({ nombre: canal.titulo || canal.nombre || marca('context.LlamadasContext.grupo', 'Grupo'), grupo: true }); setEst('llamando')
    try { await iniciarLocal(video) } catch (e) { terminar(); return toast.error(i18nT('context.LlamadasContext.no_se_pudo_acceder_a', 'No se pudo acceder a la cámara o el micrófono')) }
    if (estadoRef.current !== 'llamando') { pararLocal(); return }
    const sala = 'group-' + canal.id + '-' + (user?.id || '') + '-' + Math.random().toString(36).slice(2, 8)
    salaRef.current = sala; destRef.current = null
    getSock().emit('llamada:iniciar-grupo', { canalId: canal.id, sala, video }, (ack) => {
      if (estadoRef.current !== 'llamando') return
      if (!ack?.ok) { toast.error(ack?.error || i18nT('context.LlamadasContext.no_se_pudo_llamar_al', 'No se pudo llamar al grupo')); terminar(); return }
      unirse(sala)
      if (!ack.conectados) toast.info(i18nT('context.LlamadasContext.nadie_del_grupo_esta_conectado', 'Nadie del grupo está conectado ahora mismo'))
      // Si nadie se une en 60s, colgar (no dejar cámara/mic encendidos indefinidamente).
      timeoutRef.current = setTimeout(() => { if (estadoRef.current === 'llamando') { toast.error(i18nT('context.LlamadasContext.nadie_se_ha_unido_a', 'Nadie se ha unido a la llamada')); colgar() } }, 60000)
    })
  }

  const aceptar = async () => {
    const ent = entrante
    if (!ent) return
    // Reservar estado + consumir la entrante ANTES del await (anti doble-clic y anti doble sesión).
    limpiarTimeout(); ring(false); setEntrante(null); setEst('en-llamada')
    getSock().emit('llamada:atendida', { sala: ent.sala })   // acallar mis otras sesiones
    try { await iniciarLocal(ent.video) } catch (e) { terminar(); return toast.error(i18nT('context.LlamadasContext.no_se_pudo_acceder_a', 'No se pudo acceder a la cámara o el micrófono')) }
    if (estadoRef.current !== 'en-llamada') { pararLocal(); return }   // colgaron mientras pedía permisos
    setConVideo(ent.video); destRef.current = ent.de.id; salaRef.current = ent.sala
    unirse(ent.sala)
  }

  const rechazar = () => {
    limpiarTimeout(); ring(false)
    if (entrante) getSock().emit('llamada:rechazar', { sala: entrante.sala, aUsuarioId: entrante.de.id })
    setEntrante(null); setEst('idle')
  }

  const colgar = () => {
    const s = getSock()
    if (estadoRef.current === 'llamando' && destRef.current) s.emit('llamada:cancelar', { sala: salaRef.current, aUsuarioId: destRef.current })
    if (salaRef.current) s.emit('llamada:salir', { sala: salaRef.current })
    terminar()
  }

  const toggleMic = () => { const on = !micOn; localRef.current?.getAudioTracks().forEach(t => t.enabled = on); setMicOn(on) }
  const toggleCam = () => { const on = !camOn; localRef.current?.getVideoTracks().forEach(t => t.enabled = on); setCamOn(on) }

  // Conexión + listeners (una vez, mientras haya sesión).
  useEffect(() => {
    if (!user) { if (estadoRef.current !== 'idle') terminar(); if (_sock?.connected) _sock.disconnect(); return }
    const s = getSock()
    // (El token lo pone la función `auth` de getSock en cada conexión: aquí no se pisa con uno fijo.)
    if (!s.connected) s.connect()
    // B-914 · al volver a la app o a la red, si la conexión se cayó, se reabre (y al conectar el busca se pone al día).
    const reconectar = () => { if (!s.connected && localStorage.getItem('token')) s.connect() }
    window.addEventListener('online', reconectar)
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', reconectar)
    api.get('/llamadas/config').then(r => { if (r.data?.iceServers?.length) iceRef.current = r.data.iceServers }).catch(() => {})
    // EL TONO DE ESTA PERSONA. Primero el espejo del navegador —instantaneo, para que la primera
    // llamada de la sesion ya suene bien— y despues la preferencia de la CUENTA, que es la que
    // manda y la que vale tambien en el otro ordenador. Si falla la peticion no pasa nada: suena
    // el de siempre, que es exactamente lo que sonaba antes de que esto existiera.
    tonoRef.current = leerTonoLocal(user?.id)
    api.get('/auth/preferencias').then(r => {
      const c = tonoValido(r.data?.tono_llamada)
      if (c) { tonoRef.current = c; guardarTonoLocal(c, user?.id) }
    }).catch(() => {})

    const onEntrante = ({ sala, video, de, grupo }) => {
      if (estadoRef.current !== 'idle') { s.emit('llamada:rechazar', { sala, aUsuarioId: de.id, motivo: 'ocupado' }); return }
      setEntrante({ sala, video, de, grupo: grupo || null }); setEst('entrante'); ring(true)
      // Auto-descartar tras 40s sin coger (avisando al que llama), para no sonar indefinidamente.
      limpiarTimeout()
      timeoutRef.current = setTimeout(() => {
        if (estadoRef.current === 'entrante') { s.emit('llamada:rechazar', { sala, aUsuarioId: de.id, motivo: 'no_contesta' }); ring(false); setEntrante(null); setEst('idle') }
      }, 40000)
    }
    const onRechazada = ({ por, motivo }) => {
      // Solo aplica si AÚN estoy llamando; si ya conecté (una sesión del otro cogió), un rechazo de
      // otra de sus sesiones NO debe tirar la llamada en curso.
      if (estadoRef.current !== 'llamando') return
      const txt = motivo === 'ocupado' ? i18nT('context.LlamadasContext.esta_ocupado_en_otra_llamada', ' está ocupado en otra llamada') : motivo === 'no_contesta' ? i18nT('context.LlamadasContext.no_ha_contestado_2', ' no ha contestado') : i18nT('context.LlamadasContext.no_ha_aceptado_la_llamada', ' no ha aceptado la llamada')
      toast.error((por?.nombre || i18nT('context.LlamadasContext.el_companero_2', 'El compañero')) + txt); terminar()
    }
    const onCancelada = () => { limpiarTimeout(); ring(false); setEntrante(null); if (estadoRef.current === 'entrante') setEst('idle') }
    // Otra de MIS sesiones atendió → dejar de sonar aquí.
    const onAtendida = () => { if (estadoRef.current === 'entrante') { limpiarTimeout(); ring(false); setEntrante(null); setEst('idle') } }
    const onPeerEntro = () => { if (estadoRef.current === 'llamando') { limpiarTimeout(); setEst('en-llamada') } }
    const onPeerSalio = ({ socketId }) => cerrarPeer(socketId)
    const onSenal = async ({ de, agente, datos }) => {
      try {
        if (datos.tipo === 'offer') {
          // Oferta huérfana (llegó tras colgar, o sin llamada activa): ignorar → no crear peers zombis.
          if (estadoRef.current === 'idle' || !salaRef.current) return
          const pc = peersRef.current[de]?.pc || crearPeer(de, agente, false)
          await pc.setRemoteDescription(datos.sdp)
          await flushIce(de)                                  // aplicar ICE que llegó antes de la oferta
          const ans = await pc.createAnswer(); await pc.setLocalDescription(ans)
          s.emit('llamada:senal', { a: de, sala: salaRef.current, datos: { tipo: 'answer', sdp: pc.localDescription } })
        } else if (datos.tipo === 'answer') {
          const p = peersRef.current[de]
          if (p) { await p.pc.setRemoteDescription(datos.sdp); await flushIce(de) }
        } else if (datos.tipo === 'ice' && datos.candidate) {
          const p = peersRef.current[de]
          if (!p) return
          // Si aún no hay descripción remota, encolar; si ya la hay, aplicar de inmediato.
          if (p.pc.remoteDescription && p.pc.remoteDescription.type) { try { await p.pc.addIceCandidate(datos.candidate) } catch (_) {} }
          else p.pendientes.push(datos.candidate)
        }
      } catch (_) { /* señal fuera de orden */ }
    }
    // B-875 · EL BUSCA llega por esta misma conexión (la sala personal de cada uno). Aquí solo se reparte, como el evento
    // 'biango-busca' de la ventana: lo pintan components/busca/BuscaEntrante (a quien le buscan) y BuscaPanel (a quien
    // busca, para ver qué le contestan).
    const repartirBusca = (tipo) => (datos) => { try { window.dispatchEvent(new CustomEvent('biango-busca', { detail: { tipo, ...(datos || {}) } })) } catch (_) { } }
    const onBuscaEntrante = repartirBusca('entrante'), onBuscaParar = repartirBusca('parar'), onBuscaRespondido = repartirBusca('respondido')
    // t398 · a quien busca: en cuántos dispositivos suena, quién y dónde lo contestó, o que se quedó sin respuesta (BuscaPanel).
    const onBuscaEstado = repartirBusca('estado')
    // B-984 · «YA PUEDE PASAR»: llega por la misma conexión a quien lleva esa agenda en el modo auxiliar; lo pinta y lo hace
    // vibrar pages/ModoAuxiliar (evento 'biango-paciente-listo' de la ventana).
    const onPacienteListo = (datos) => { try { window.dispatchEvent(new CustomEvent('biango-paciente-listo', { detail: datos || {} })) } catch (_) { } }
    // t530 · B-2268 · EL PACIENTE HA PASADO A ESTE GABINETE: llega solo a este ordenador; lo abre components/PuestoGabinete.
    const onGabinetePaciente = (datos) => { try { window.dispatchEvent(new CustomEvent('biango-gabinete-paciente', { detail: datos || {} })) } catch (_) { } }
    // t562 · B-2386 · A QUIEN HA PASADO LA CITA: la ficha no se ha abierto en ningún ordenador, y por qué (lo pinta components/PuestoGabinete).
    const onGabineteSinAbrir = (datos) => { try { window.dispatchEvent(new CustomEvent('biango-gabinete-sin-abrir', { detail: datos || {} })) } catch (_) { } }
    s.on('llamada:entrante', onEntrante); s.on('llamada:rechazada', onRechazada); s.on('llamada:cancelada', onCancelada)
    s.on('llamada:atendida', onAtendida); s.on('llamada:peer-entro', onPeerEntro); s.on('llamada:peer-salio', onPeerSalio); s.on('llamada:senal', onSenal)
    s.on('busca:entrante', onBuscaEntrante); s.on('busca:parar', onBuscaParar); s.on('busca:respondido', onBuscaRespondido)
    s.on('busca:estado', onBuscaEstado)
    s.on('paciente:listo', onPacienteListo)
    s.on('gabinete:paciente', onGabinetePaciente)
    s.on('gabinete:sin-abrir', onGabineteSinAbrir)
    return () => {
      window.removeEventListener('online', reconectar)
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', reconectar)
      s.off('llamada:entrante', onEntrante); s.off('llamada:rechazada', onRechazada); s.off('llamada:cancelada', onCancelada)
      s.off('llamada:atendida', onAtendida); s.off('llamada:peer-entro', onPeerEntro); s.off('llamada:peer-salio', onPeerSalio); s.off('llamada:senal', onSenal)
      s.off('busca:entrante', onBuscaEntrante); s.off('busca:parar', onBuscaParar); s.off('busca:respondido', onBuscaRespondido)
      s.off('busca:estado', onBuscaEstado)
      s.off('paciente:listo', onPacienteListo)
      s.off('gabinete:paciente', onGabinetePaciente)
      s.off('gabinete:sin-abrir', onGabineteSinAbrir)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  return (
    <LlamadasContext.Provider value={{ estado, llamar, llamarGrupo }}>
      {children}
      {estado === 'entrante' && entrante && <LlamadaEntrante de={entrante.de} grupo={entrante.grupo} video={entrante.video} onAceptar={aceptar} onRechazar={rechazar} />}
      {(estado === 'llamando' || estado === 'en-llamada') && (
        <LlamadaEnCurso estado={estado} llamandoA={llamandoA} remotos={remotos} localStream={localStream}
          micOn={micOn} camOn={camOn} conVideo={conVideo} onMic={toggleMic} onCam={toggleCam} onColgar={colgar} />
      )}
    </LlamadasContext.Provider>
  )
}

const avatarSrc = (b64) => `data:image/${(b64 || '').startsWith('iVBOR') ? 'png' : 'jpeg'};base64,${b64}`

function Avatar({ nombre, avatar, size = 64 }) {
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: '#334155', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: size * 0.4, overflow: 'hidden', flexShrink: 0 }}>
      {avatar ? <img src={avatarSrc(avatar)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (nombre || '?').trim()[0]?.toUpperCase() || '?'}
    </div>
  )
}

// Llamada entrante: avatar + nombre + aceptar/rechazar, con tono sonando.
function LlamadaEntrante({ de, grupo, video, onAceptar, onRechazar }) {
  return (
    <div className="modal-safe" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ background: '#0f172a', color: '#fff', borderRadius: '20px', padding: '30px 28px', textAlign: 'center', width: '320px', maxWidth: 'calc(92vw / var(--z, 1))', boxShadow: '0 24px 70px rgba(0,0,0,0.5)' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '14px' }}><Avatar nombre={grupo || de.nombre} avatar={grupo ? null : de.avatar_b64} size={84} /></div>
        <div style={{ fontSize: '18px', fontWeight: 800 }}>{grupo || de.nombre}</div>
        {grupo && <div style={{ fontSize: '12.5px', opacity: 0.75, marginTop: '2px' }}>{tx('context.LlamadasContext.llamada_de_grupo', 'Llamada de grupo · {{nombre}}', { nombre: de.nombre })}</div>}
        <div style={{ fontSize: '13px', opacity: 0.8, marginTop: '6px', animation: 'llampulse 1.4s infinite' }}>{video ? i18nT('context.LlamadasContext.videollamada_entrante', 'Videollamada entrante…') : i18nT('context.LlamadasContext.llamada_entrante', 'Llamada entrante…')}</div>
        <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', marginTop: '24px' }}>
          <button onClick={onRechazar} title={i18nT('context.LlamadasContext.rechazar', 'Rechazar')} style={btnRedondo('#dc2626')}><Icon n="phoneOff" s={24} /></button>
          <button onClick={onAceptar} title={i18nT('context.LlamadasContext.aceptar', 'Aceptar')} style={btnRedondo('#16a34a')}><Icon n={video ? 'video' : 'phone'} s={24} /></button>
        </div>
      </div>
      <style>{`@keyframes llampulse{0%,100%{opacity:.85}50%{opacity:.4}}`}</style>
    </div>
  )
}

// Llamada en curso: rejilla de vídeo (remotos + mi PiP) + barra de controles.
function LlamadaEnCurso({ estado, llamandoA, remotos, localStream, micOn, camOn, conVideo, onMic, onCam, onColgar }) {
  const conRemoto = remotos.some(r => r.stream)
  const cols = remotos.length <= 1 ? 1 : 2
  return (
    <div className="modal-safe visor-oscuro" style={{ position: 'fixed', inset: 0, zIndex: 2900, background: 'rgba(2,6,23,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '12px' }}>
      <div style={{ position: 'relative', width: 'min(860px, 100%)', height: 'min(620px, 100%)', background: '#0b1220', borderRadius: '18px', overflow: 'hidden', boxShadow: '0 24px 80px rgba(0,0,0,0.6)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ flex: 1, display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: '6px', padding: '6px', minHeight: 0 }}>
          {estado === 'llamando' && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#fff', gap: '12px' }}>
              <Avatar nombre={llamandoA?.nombre} avatar={llamandoA?.avatar_b64} size={90} />
              <div style={{ fontSize: '17px', fontWeight: 700 }}>{tx('context.LlamadasContext.llamando_a', 'Llamando a {{x}}', { x: tDato(llamandoA?.nombre) || '…' })}</div>
              <div style={{ fontSize: '13px', opacity: 0.7, animation: 'llampulse 1.4s infinite' }}>{i18nT('context.LlamadasContext.esperando_a_que_responda', 'Esperando a que responda…')}</div>
            </div>
          )}
          {remotos.map(r => (
            <div key={r.socketId} style={tileBox}>
              {r.stream ? <VideoTile stream={r.stream} muted={false} /> :
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '8px' }}><Avatar nombre={r.agente?.nombre} avatar={r.agente?.avatar_b64} size={64} /><span style={{ color: '#cbd5e1', fontSize: '12px' }}>{i18nT('context.LlamadasContext.conectando', 'Conectando…')}</span></div>}
              <span style={tileNombre}>{r.agente?.nombre || i18nT('context.LlamadasContext.companero', 'Compañero')}</span>
            </div>
          ))}
        </div>

        {/* Mi cámara (PiP) */}
        {localStream && conVideo && (
          <div style={{ position: 'absolute', bottom: '78px', right: '14px', width: '132px', height: '99px', borderRadius: '12px', overflow: 'hidden', border: '2px solid rgba(255,255,255,0.5)', background: '#000', boxShadow: '0 4px 14px rgba(0,0,0,0.5)' }}>
            <VideoTile stream={localStream} muted mirror />
            {!camOn && <div style={{ position: 'absolute', inset: 0, background: '#0b1220', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: '11px' }}>{i18nT('context.LlamadasContext.camara_off', 'Cámara off')}</div>}
          </div>
        )}

        {/* Controles */}
        <div style={{ display: 'flex', gap: '14px', justifyContent: 'center', padding: '14px', background: 'rgba(0,0,0,0.35)' }}>
          <button onClick={onMic} title={micOn ? i18nT('context.LlamadasContext.silenciar_microfono', 'Silenciar micrófono') : i18nT('context.LlamadasContext.activar_microfono', 'Activar micrófono')} style={btnRedondo(micOn ? '#334155' : '#dc2626', 52)}><Icon n={micOn ? 'mic' : 'micOff'} s={21} /></button>
          {conVideo && <button onClick={onCam} title={camOn ? i18nT('context.LlamadasContext.apagar_camara', 'Apagar cámara') : i18nT('context.LlamadasContext.encender_camara', 'Encender cámara')} style={btnRedondo(camOn ? '#334155' : '#dc2626', 52)}><Icon n={camOn ? 'video' : 'videoOff'} s={21} /></button>}
          <button onClick={onColgar} title={i18nT('context.LlamadasContext.colgar', 'Colgar')} style={btnRedondo('#dc2626', 52)}><Icon n="phoneOff" s={21} /></button>
        </div>
      </div>
      <style>{`@keyframes llampulse{0%,100%{opacity:.85}50%{opacity:.4}}`}</style>
    </div>
  )
}

// <video> que engancha un MediaStream (no se puede pasar por src).
function VideoTile({ stream, muted, mirror }) {
  const ref = useRef(null)
  useEffect(() => { if (ref.current && stream) ref.current.srcObject = stream }, [stream])
  return <video ref={ref} autoPlay playsInline muted={muted} style={{ width: '100%', height: '100%', objectFit: 'cover', transform: mirror ? 'scaleX(-1)' : 'none', background: '#000' }} />
}

const tileBox = { position: 'relative', background: '#000', borderRadius: '12px', overflow: 'hidden', minHeight: 0 }
const tileNombre = { position: 'absolute', bottom: '8px', left: '10px', color: '#fff', fontSize: '12px', fontWeight: 600, background: 'rgba(0,0,0,0.45)', padding: '2px 8px', borderRadius: '8px' }
const btnRedondo = (bg, size = 56) => ({ width: size, height: size, borderRadius: '50%', border: 'none', background: bg, color: '#fff', fontSize: size * 0.4, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.3)' })
